import { API_BASE_URL } from "./config";

const primaryUserEnum = [
  "I am the primary user",
  "Someone else",
  "Shared responsibility",
];

const mainIssueEnum = [
  "D-Bonding",
  "Contamination",
  "Dust",
  "Debree",
  "Heat",
  "Low Hours",
];

const wetOrDryApplicationEnum = ["Wet", "Dry"];

const mattersMostEnum = ["Durability", "Availability", "Price"];

const yesNoEnum = ["Yes", "No"];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[A-Za-z\d\W]{8,}$/;
const nameRegex = /^[a-zA-Z\s]+$/;

const conversionMap = {
  _id: "ID",
  id: "Lead ID",
  company: "Company Name",
  name: "Name",
  email: "Email Address",
  phone: "Phone Number",
  brandMake: "Brand/Make",
  brand_make: "Brand/Make", // snake_case variant
  model: "Model",
  quantity: "Quantity",
  rebuildOnSite: "Rebuild On Site?",
  rebuild_on_site: "Rebuild On Site?", // snake_case variant
  whoRebuild: "Rebuild By",
  who_rebuild: "Rebuild By", // snake_case variant
  primaryUser: "Primary User",
  primary_user: "Primary User", // snake_case variant
  hoursGotIt: "Operating Hours",
  hours_got_it: "Operating Hours", // snake_case variant
  mainIssue: "Primary Issue",
  main_issue: "Primary Issue", // snake_case variant
  oemPartNumber: "OEM Part #",
  oem_part_number: "OEM Part #", // snake_case variant
  interestedInNewMaterial: "Interested in New Material?",
  interested_in_new_material: "Interested in New Material?", // snake_case variant
  needExpertOpinion: "Need Expert Opinion?",
  need_expert_opinion: "Need Expert Opinion?", // snake_case variant
  openToNewApplication: "New Application",
  open_to_new_application: "New Application", // snake_case variant
  wetOrDryApplication: "Application Type",
  wet_or_dry_application: "Application Type", // snake_case variant
  hoursOfDowntime: "Downtime (hrs)",
  hours_of_downtime: "Downtime (hrs)", // snake_case variant
  amountLossByThroughputHr: "Loss per Throughput Hour ($)",
  amount_loss_by_throughput_hr: "Loss per Throughput Hour ($)", // snake_case variant
  mattersMost: "Priority",
  matters_most: "Priority", // snake_case variant
  shiftsPerDay: "Shifts per Day",
  shifts_per_day: "Shifts per Day", // snake_case variant
  weeksPerMonth: "Weeks per Month",
  weeks_per_month: "Weeks per Month", // snake_case variant
  monthsPerYear: "Months per Year",
  months_per_year: "Months per Year", // snake_case variant
  costExistingPart: "Cost of Existing Part ($)",
  cost_existing_part: "Cost of Existing Part ($)", // snake_case variant
  partsPerEquipment: "Parts per Equipment",
  parts_per_equipment: "Parts per Equipment", // snake_case variant
  operativeDaysPerWeek: "Operating Days per Week",
  operative_days_per_week: "Operating Days per Week", // snake_case variant
  totalSavings: "Total Savings",
  total_savings: "Total Savings", // snake_case variant
  contacted: "Contacted",
  contactedAt: "Contacted At",
  contacted_at: "Contacted At", // snake_case variant
  contactedBy: "Contacted By",
  contacted_by: "Contacted By", // snake_case variant
  contactStatus: "Deal Status",
  contact_status: "Deal Status", // snake_case variant
  dealStatus: "Deal Status",
  deal_status: "Deal Status", // snake_case variant
  createdAt: "Created At",
  created_at: "Created At", // snake_case variant
  updatedAt: "Updated At",
  updated_at: "Updated At", // snake_case variant
  score: "Lead Score",
  notes: "Notes",
  source: "Lead Source",
  isActive: "Active Status",
  is_active: "Active Status", // snake_case variant
};

// Page-based form structure for new 3-page flow
const formSteps = {
  // PAGE 1: Financial/Quantity Questions + Savings Calculation + Email
  page1: [
    {
      label: "How many equipment/machine/vehicle do you have?",
      name: "quantity",
      type: "number",
      placeholder: "Enter quantity (minimum 1)",
      required: true,
      min: 1,
      validate: (value) => {
        if (!value || value === "" || value === null || value === undefined)
          return "Quantity is required";
        const numValue = parseInt(value);
        if (isNaN(numValue) || numValue < 1)
          return "Quantity must be at least 1";
        return "";
      },
    },
    {
      label: "Parts per Equipment",
      name: "partsPerEquipment",
      type: "number",
      placeholder: "Enter parts per equipment (minimum 1)",
      required: true,
      min: 1,
      validate: (value) => {
        if (!value || value === "" || value === null || value === undefined)
          return "Parts per Equipment is required";
        const numValue = parseInt(value);
        if (isNaN(numValue) || numValue < 1)
          return "Parts per Equipment must be at least 1";
        return "";
      },
    },
    {
      label: "$ Cost of Existing Part",
      name: "costExistingPart",
      type: "number",
      placeholder: "Enter cost of existing part (must be positive)",
      required: true,
      min: 0,
      step: 0.01,
      validate: (value) => {
        if (!value || value === "" || value === null || value === undefined)
          return "Cost of Existing Part is required";
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0)
          return "Cost of Existing Part must be a positive number";
        return "";
      },
    },
    {
      label: "How many hours do you get out of that part? (How long it lasts?)",
      name: "hoursGotIt",
      type: "number",
      placeholder: "Enter hours got it (must be positive)",
      required: true,
      min: 0,
      validate: (value) => {
        if (!value || value === "" || value === null || value === undefined)
          return "Hours Got It is required";
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0)
          return "Hours Got It must be a positive number";
        return "";
      },
    },
    {
      label: "Hours of downtime associated for changing the part?",
      name: "hoursOfDowntime",
      type: "number",
      placeholder: "Enter hours of downtime (must be positive)",
      required: true,
      min: 0,
      validate: (value) => {
        if (!value || value === "" || value === null || value === undefined)
          return "Hours of Downtime is required";
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0)
          return "Hours of Downtime must be a positive number";
        return "";
      },
    },
    {
      label: "$ amount Loss/throughput per hour?",
      name: "amountLossByThroughputHr",
      type: "number",
      placeholder: "Enter amount loss by throughput/hr (must be positive)",
      required: true,
      min: 0,
      step: 0.01,
      validate: (value) => {
        if (!value || value === "" || value === null || value === undefined)
          return "Amount loss by throughput/hr is required";
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0)
          return "Amount loss by throughput/hr must be a positive number";
        return "";
      },
    },
    {
      label: "Number of 8 hours shifts in a day?",
      name: "shiftsPerDay",
      type: "number",
      placeholder: "Enter number of shifts (1-3)",
      required: true,
      min: 1,
      max: 3,
      validate: (value) => {
        if (!value || value === "" || value === null || value === undefined)
          return "Shifts per Day is required";
        const numValue = parseInt(value);
        if (isNaN(numValue) || numValue < 1 || numValue > 3)
          return "Shifts per Day must be between 1 and 3";
        return "";
      },
    },
    // After calculation, show savings and ask for email
    {
      label: "Email",
      name: "email",
      type: "email",
      placeholder: "Enter your email",
      required: true,
      validate: (value) => {
        if (!value || !value.trim() || !emailRegex.test(value)) {
          return "Email is required and must be valid";
        }
        return "";
      },
    },
  ],

  // PAGE 2: Email Validation/Confirmation
  page2: [
    {
      label: "Email Validation",
      name: "emailValidation",
      type: "validation",
      description: "For privacy protection, please validate your email below",
      required: true,
    },
  ],

  // PAGE 3: Split Layout - Left Side (Company Details) + Right Side (New Application)
  page3: {
    leftSide: [
      {
        label: "Company Name",
        name: "company",
        type: "text",
        placeholder: "Enter your company name",
        required: true,
        validate: (value) => {
          if (!value || !value.trim()) {
            return "Company name is required";
          }
          return "";
        },
      },
      {
        label: "Phone",
        name: "phone",
        type: "text",
        placeholder: "Enter your phone number",
        required: false,
        validate: () => "",
      },
      {
        label: "Name",
        name: "name",
        type: "text",
        placeholder: "Enter your name",
        required: true,
        validate: (value) => {
          if (!value || !value.trim() || !nameRegex.test(value)) {
            return "Name is required and must be valid";
          }
          return "";
        },
      },
      {
        label: "What is the Brand/Make of your Equipment?",
        name: "brandMake",
        type: "text",
        placeholder: "Enter brand and make of your equipment",
        required: false,
        validate: () => "",
      },
      {
        label: "Model",
        name: "model",
        type: "text",
        placeholder: "Enter model",
        required: false,
        validate: () => "",
      },
      {
        label: "Do you rebuild on-site?",
        name: "rebuildOnSite",
        type: "radio",
        options: yesNoEnum,
        required: false,
        validate: () => "",
      },
      {
        label: "Who rebuilds?",
        name: "whoRebuild",
        type: "text",
        placeholder: "Who does the rebuild?",
        required: false,
        validate: () => "",
      },
      {
        label: "Are you the primary user? Are these your equipments?",
        name: "primaryUser",
        type: "select",
        options: primaryUserEnum,
        placeholder: "Select a user...",
        required: true,
        validate: (value) => {
          if (!value) return "Primary User selection is required";
          return "";
        },
      },
      {
        label: "Wet or Dry Application",
        name: "wetOrDryApplication",
        type: "select",
        options: wetOrDryApplicationEnum,
        placeholder: "Select application type...",
        required: true,
        validate: (value) => {
          if (!value) return "Wet or Dry Application selection is required";
          return "";
        },
      },
      {
        label: "What's the main issue right now?",
        name: "mainIssue",
        type: "select",
        options: mainIssueEnum,
        placeholder: "Select main issue...",
        required: false,
        validate: () => "",
      },
      {
        label: "OEM Part #",
        name: "oemPartNumber",
        type: "text",
        placeholder: "Enter OEM Part Number",
        required: false,
        validate: () => "",
      },
      {
        label:
          "Are you interested in using new High-Tech Material for better performance?",
        name: "interestedInNewMaterial",
        type: "radio",
        options: yesNoEnum,
        required: false,
        validate: () => "",
      },
      {
        label: "Do you need an expert's opinion?",
        name: "needExpertOpinion",
        type: "radio",
        options: yesNoEnum,
        required: false,
        validate: () => "",
      },
      {
        label: "What matters most?",
        name: "mattersMost",
        type: "select",
        options: mattersMostEnum,
        placeholder: "Select what matters most...",
        required: true,
        validate: (value) => {
          if (!value) return "Matters Most selection is required";
          return "";
        },
      },
    ],
    rightSide: [
      {
        label: "None of the above, I have a new application for you.",
        name: "openToNewApplication",
        type: "radio",
        options: yesNoEnum,
        required: false,
        validate: () => "",
      },
    ],
  },
};

// Updated flat structure with grouped financial questions
const formStepsFlat = [
  // Group the 7 financial questions into a single multi-field step
  {
    label: "Equipment & Financial Information",
    name: "financialInfo",
    type: "multi",
    fields: [
      {
        label: "How many equipment/machine/vehicle do you have?",
        name: "quantity",
        type: "number",
        placeholder: "Enter quantity (minimum 1)",
        required: true,
        min: 1,
        validate: (value) => {
          if (!value || value === "" || value === null || value === undefined)
            return "Quantity is required";
          const numValue = parseInt(value);
          if (isNaN(numValue) || numValue < 1)
            return "Quantity must be at least 1";
          return "";
        },
      },
      {
        label: "Parts per Equipment",
        name: "partsPerEquipment",
        type: "number",
        placeholder: "Enter parts per equipment (minimum 1)",
        required: true,
        min: 1,
        validate: (value) => {
          if (!value || value === "" || value === null || value === undefined)
            return "Parts per Equipment is required";
          const numValue = parseInt(value);
          if (isNaN(numValue) || numValue < 1)
            return "Parts per Equipment must be at least 1";
          return "";
        },
      },
      {
        label: "$ Cost of Existing Part",
        name: "costExistingPart",
        type: "number",
        placeholder: "Enter cost of existing part (must be positive)",
        required: true,
        min: 0,
        step: 0.01,
        validate: (value) => {
          if (!value || value === "" || value === null || value === undefined)
            return "Cost of Existing Part is required";
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0)
            return "Cost of Existing Part must be a positive number";
          return "";
        },
      },
      {
        label:
          "How many hours do you get out of that part? (How long it lasts?)",
        name: "hoursGotIt",
        type: "number",
        placeholder: "Enter hours got it (must be positive)",
        required: true,
        min: 0,
        validate: (value) => {
          if (!value || value === "" || value === null || value === undefined)
            return "Hours Got It is required";
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0)
            return "Hours Got It must be a positive number";
          return "";
        },
      },
      {
        label: "Hours of downtime associated for changing the part?",
        name: "hoursOfDowntime",
        type: "number",
        placeholder: "Enter hours of downtime (must be positive)",
        required: true,
        min: 0,
        validate: (value) => {
          if (!value || value === "" || value === null || value === undefined)
            return "Hours of Downtime is required";
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0)
            return "Hours of Downtime must be a positive number";
          return "";
        },
      },
      {
        label: "$ amount Loss/throughput per hour?",
        name: "amountLossByThroughputHr",
        type: "number",
        placeholder: "Enter amount loss by throughput/hr (must be positive)",
        required: true,
        min: 0,
        step: 0.01,
        validate: (value) => {
          if (!value || value === "" || value === null || value === undefined)
            return "Amount loss by throughput/hr is required";
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0)
            return "Amount loss by throughput/hr must be a positive number";
          return "";
        },
      },
      {
        label: "Number of 8 hours shifts in a day?",
        name: "shiftsPerDay",
        type: "number",
        placeholder: "Enter number of shifts (1-3)",
        required: true,
        min: 1,
        max: 3,
        validate: (value) => {
          if (!value || value === "" || value === null || value === undefined)
            return "Shifts per Day is required";
          const numValue = parseInt(value);
          if (isNaN(numValue) || numValue < 1 || numValue > 3)
            return "Shifts per Day must be between 1 and 3";
          return "";
        },
      },
    ],
    required: true,
    validate: (formData) => {
      // Validate that all fields in the financial info section are filled
      const requiredFields = [
        "quantity",
        "partsPerEquipment",
        "costExistingPart",
        "hoursGotIt",
        "hoursOfDowntime",
        "amountLossByThroughputHr",
        "shiftsPerDay",
      ];

      for (const field of requiredFields) {
        if (
          !formData[field] ||
          formData[field] === "" ||
          formData[field] === null ||
          formData[field] === undefined
        ) {
          return `Please fill in all financial information fields`;
        }
      }
      return "";
    },
  },
  // Email field (for showing savings and collecting email)
  {
    label: "Email",
    name: "email",
    type: "email",
    placeholder: "Enter your email",
    required: true,
    validate: (value) => {
      if (!value || !value.trim() || !emailRegex.test(value)) {
        return "Email is required and must be valid";
      }
      return "";
    },
  },
  // Split layout step - Company details on left, New application on right
  {
    label: "Additional Information",
    name: "splitLayout",
    type: "split",
    leftSide: [
      {
        label: "Company Name",
        name: "company",
        type: "text",
        placeholder: "Enter your company name",
        required: true,
        validate: (value) => {
          if (!value || !value.trim()) {
            return "Company name is required";
          }
          return "";
        },
      },
      {
        label: "Phone",
        name: "phone",
        type: "text",
        placeholder: "Enter your phone number",
        required: false,
        validate: () => "",
      },
      {
        label: "Name",
        name: "name",
        type: "text",
        placeholder: "Enter your name",
        required: true,
        validate: (value) => {
          if (!value || !value.trim() || !nameRegex.test(value)) {
            return "Name is required and must be valid";
          }
          return "";
        },
      },
      {
        label: "What is the Brand/Make of your Equipment?",
        name: "brandMake",
        type: "text",
        placeholder: "Enter brand and make of your equipment",
        required: false,
        validate: () => "",
      },
      {
        label: "Model",
        name: "model",
        type: "text",
        placeholder: "Enter model",
        required: false,
        validate: () => "",
      },
      {
        label: "Do you rebuild on-site?",
        name: "rebuildOnSite",
        type: "radio",
        options: yesNoEnum,
        required: false,
        validate: () => "",
      },
      {
        label: "Who rebuilds?",
        name: "whoRebuild",
        type: "text",
        placeholder: "Who does the rebuild?",
        required: false,
        validate: () => "",
      },
      {
        label: "Are you the primary user? Are these your equipments?",
        name: "primaryUser",
        type: "select",
        options: primaryUserEnum,
        placeholder: "Select a user...",
        required: true,
        validate: (value) => {
          if (!value) return "Primary User selection is required";
          return "";
        },
      },
      {
        label: "Wet or Dry Application",
        name: "wetOrDryApplication",
        type: "select",
        options: wetOrDryApplicationEnum,
        placeholder: "Select application type...",
        required: true,
        validate: (value) => {
          if (!value) return "Wet or Dry Application selection is required";
          return "";
        },
      },
      {
        label: "What's the main issue right now?",
        name: "mainIssue",
        type: "select",
        options: mainIssueEnum,
        placeholder: "Select main issue...",
        required: false,
        validate: () => "",
      },
      {
        label: "OEM Part #",
        name: "oemPartNumber",
        type: "text",
        placeholder: "Enter OEM Part Number",
        required: false,
        validate: () => "",
      },
      {
        label:
          "Are you interested in using new High-Tech Material for better performance?",
        name: "interestedInNewMaterial",
        type: "radio",
        options: yesNoEnum,
        required: false,
        validate: () => "",
      },
      {
        label: "Do you need an expert's opinion?",
        name: "needExpertOpinion",
        type: "radio",
        options: yesNoEnum,
        required: false,
        validate: () => "",
      },
      {
        label: "What matters most?",
        name: "mattersMost",
        type: "select",
        options: mattersMostEnum,
        placeholder: "Select what matters most...",
        required: true,
        validate: (value) => {
          if (!value) return "Matters Most selection is required";
          return "";
        },
      },
    ],
    rightSide: [
      {
        label: "None of the above, I have a new application for you.",
        name: "openToNewApplication",
        type: "radio",
        options: yesNoEnum,
        required: false,
        validate: () => "",
      },
    ],
    required: true,
    validate: (formData) => {
      // Validation is now handled in the component for better control
      // This is kept for backward compatibility but should be minimal
      const isNewApplication = formData.openToNewApplication === "Yes";

      if (!isNewApplication) {
        // If not new application, at least company and name should be filled
        if (!formData.company || !formData.name) {
          return "Please fill in required fields or select the new application option";
        }
      }
      return "";
    },
  },
];

// API Base URL from config
const BASE_URL = API_BASE_URL;

export {
  primaryUserEnum,
  mainIssueEnum,
  wetOrDryApplicationEnum,
  mattersMostEnum,
  yesNoEnum,
  emailRegex,
  passwordRegex,
  nameRegex,
  BASE_URL,
  conversionMap,
  formSteps,
  formStepsFlat, // Legacy flat structure for backward compatibility
};
